import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AcceptStaffInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'checking' | 'not_logged_in' | 'accepting' | 'success' | 'error'>('checking');
  const [message, setMessage] = useState('');
  const [businessName, setBusinessName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No invite token found. Please use the link from your invitation email.');
      return;
    }

    // Check if user is logged in
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) {
      setStatus('not_logged_in');
      return;
    }

    // User is logged in — accept the invite
    acceptInvite(authToken);
  }, [token]);

  const acceptInvite = async (authToken: string) => {
    setStatus('accepting');
    try {
      const response = await fetch('/backend/scheduling/staff/accept-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Invite accepted!');
        setBusinessName(data.businessName || '');
        // Redirect to staff dashboard after 2 seconds
        setTimeout(() => navigate('/scheduling/staff'), 2000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to accept invite');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  const handleLoginRedirect = () => {
    // Redirect to login, preserving the invite token so we can come back
    const returnUrl = '/scheduling/accept-invite?token=' + encodeURIComponent(token || '');
    navigate('/login?redirect=' + encodeURIComponent(returnUrl));
  };

  const handleRegisterRedirect = () => {
    const returnUrl = '/scheduling/accept-invite?token=' + encodeURIComponent(token || '');
    navigate('/register?redirect=' + encodeURIComponent(returnUrl));
  };

  return (
    <div style={{
      maxWidth: '480px', margin: '80px auto', padding: '40px',
      backgroundColor: 'white', borderRadius: '16px',
      border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      textAlign: 'center',
    }}>
      <h1 style={{ color: '#3b82f6', margin: '0 0 8px', fontSize: '1.5rem' }}>Larry's Help</h1>
      <p style={{ color: '#6b7280', margin: '0 0 30px', fontSize: '0.9rem' }}>Staff Invitation</p>

      {status === 'checking' && (
        <p style={{ color: '#6b7280' }}>Checking your login status...</p>
      )}

      {status === 'not_logged_in' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>&#9993;</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem' }}>You've Been Invited!</h2>
          <p style={{ color: '#6b7280', margin: '0 0 24px' }}>
            Log in or create a free account to accept this invitation and view your bookings.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={handleLoginRedirect}
              style={{
                padding: '12px 28px', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer',
                fontWeight: '600', fontSize: '1rem',
              }}
            >
              Log In
            </button>
            <button
              onClick={handleRegisterRedirect}
              style={{
                padding: '12px 28px', backgroundColor: 'white', color: '#3b82f6',
                border: '2px solid #3b82f6', borderRadius: '8px', cursor: 'pointer',
                fontWeight: '600', fontSize: '1rem',
              }}
            >
              Create Account
            </button>
          </div>
        </>
      )}

      {status === 'accepting' && (
        <p style={{ color: '#6b7280' }}>Accepting your invitation...</p>
      )}

      {status === 'success' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>&#9989;</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem', color: '#059669' }}>Welcome!</h2>
          <p style={{ color: '#374151', margin: '0 0 8px' }}>{message}</p>
          {businessName && (
            <p style={{ color: '#6b7280', margin: '0 0 16px' }}>
              You now have staff access to <strong>{businessName}</strong>.
            </p>
          )}
          <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Redirecting to your dashboard...</p>
        </>
      )}

      {status === 'error' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>&#10060;</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem', color: '#dc2626' }}>Something went wrong</h2>
          <p style={{ color: '#6b7280', margin: '0 0 20px' }}>{message}</p>
          <button
            onClick={() => navigate('/scheduling')}
            style={{
              padding: '10px 24px', backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db', borderRadius: '8px',
              cursor: 'pointer', fontSize: '0.95rem',
            }}
          >
            Go to Scheduling
          </button>
        </>
      )}
    </div>
  );
};

export default AcceptStaffInvite;
